(ns client-portal.client.voice-commerce.orders.views
  (:require [clojure.string :as s]
            [client-portal.client.layout :as l]
            [client-portal.client.voice-commerce.orders.data :as orders.d]
            [client-portal.client.voice-commerce.orders.drawer.views]
            [client-portal.client.voice-insights.empty-state :as empty-state]
            [shared.core.amelia-light.components.components :as alc]
            [shared.core.reactive :as re]
            [shared.core.style :as style]
            [shared.core.util :as util]
            [shared.core.utils.currency :as currency]))

(def ^:private total-column-width
  "Width of the Total column. The expanded panel reuses it so its Total lands in the
   same band as the cell above."
  90)

(def ^:private cell-padding-left
  "What `table-cell-style` insets a cell's content by — the panel's Total matches it."
  12)

(def ^:private columns
  "The table's columns, left to right: the header label, its width, and how the cell
   reads its value off an order row. `:money?` columns arrive as dollars (the query
   converts the cents) and only affect formatting — every column is left aligned.
   `:hide-with-panel?` columns drop out while the detail panel has the table squeezed,
   and a `:panel-flex` column takes the width they leave behind."
  [{:label "Order ID" :flex "0 0 130px" :panel-flex "1 0 130px" :key :order-id}
   {:label "Timestamp" :flex "0 0 130px" :key :occurred-at}
   {:label "Restaurant" :flex "1 0 160px" :key :restaurant-name :hide-with-panel? true}
   {:label "Guest" :flex "0 0 120px" :key :guest-name :hide-with-panel? true}
   {:label "Status" :flex "0 0 110px" :key :order-status}
   {:label "Branded" :flex "0 0 90px" :key :branded}
   {:label "Items" :flex "0 0 70px" :key :item-count :numeric? true}
   {:label "Subtotal" :flex "0 0 90px" :key :subtotal :money? true}
   {:label "Total" :flex (str "0 0 " total-column-width "px") :key :total :money? true}])

(defn- visible-columns
  "`columns`, minus the ones that give up their space to the detail panel, with
   `:panel-flex` swapped in for whichever column absorbs it."
  [panel-open?]
  (cond->> columns
           panel-open?
           (remove :hide-with-panel?)

           panel-open?
           (mapv (fn [{:keys [panel-flex] :as column}]
                   (cond-> column
                           panel-flex
                           (assoc :flex panel-flex))))))

(defn- expand-toggle [{:keys [expanded? on-toggle]}]
  [alc/up-ghost-button {:icon-type (if expanded? :ChevronTopIcon :ChevronDownIcon)
                        :icon "md"
                        ;; The row opens the detail panel, so expanding must not also
                        ;; count as a row click.
                        :on-click (fn [e]
                                    (util/stop-event e)
                                    (on-toggle))
                        :style {:padding 8}}])

(defn- money-label
  "An amount the query already converted from cents, in the tenant's currency."
  [v]
  (if (nil? v)
    "-"
    (str (currency/get-currency-symbol) (util/readable-number v))))

(def ^:private expand-column-width
  "Width of the trailing chevron column. Doubles as the detail panel's right padding
   so the breakdown ends where the Total cell above it does."
  45)

(def ^:private fee-breakdown
  "What an expanded order itemizes, left to right. Total is `:column-aligned?`: it
   takes the Total column's box instead of joining the right-aligned run, so the two
   amounts sit in the same place."
  [{:label "Subtotal" :key :subtotal}
   {:label "Tax" :key :tax}
   {:label "Delivery Fee" :key :delivery-fee}
   {:label "Service Fee" :key :service-fee}
   {:label "Base Commission" :key :base-commission}
   {:label "Total" :key :total :column-aligned? true}])

(defn details-panel [order]
  ;; Right padding is exactly the chevron column, so the last entry ends where the
  ;; Total cell above it does.
  (into [:div {:style {:background-color (style/alc-color "Background/page")
                       :flex 1
                       :display :flex
                       :justify-content :flex-end
                       :align-items :flex-start
                       :gap 32
                       :padding (str "16px " expand-column-width "px 16px 12px")}}]
        (mapv (fn [{:keys [label key column-aligned?]}]
                [:div {:key label
                       :style (merge {:display :flex
                                      :flex-direction :column
                                      :gap 2}
                                     (if column-aligned?
                                       ;; The Total cell's own box: same basis, same inset.
                                       {:align-items :flex-start
                                        :flex (str "0 0 " total-column-width "px")
                                        :padding-left cell-padding-left}
                                       {:align-items :flex-end}))}
                 [:span {:style {:color (style/alc-color "Text/secondary")
                                 :font-size 12
                                 :line-height 1.33
                                 :white-space :nowrap}}
                  label]
                 ;; Matches a table cell: same size, same line height, and Total keeps
                 ;; the color it has in the row above.
                 [:span {:style {:color (style/alc-color (if column-aligned?
                                                           "Common/White"
                                                           "Text/primary"))
                                 :font-size 12
                                 :line-height 1.5}}
                  (money-label (get order key))]])
              fee-breakdown)))

(def ^:private order-status->icon
  "Icon and its color per order status — the status text keeps the default cell color."
  {"completed" {:icon-type :check-circle :color "Common/success"}
   "refunded" {:icon-type :undo :color "Chart/orange"}
   "created" {:icon-type :schedule :color "Chart/tech_blue"}
   "cancelled" {:icon-type :cancel :color "Icons/secondary"}
   "failed" {:icon-type :error :color "Common/error"}})

(defn- status-cell [status]
  (let [{:keys [icon-type color]} (get order-status->icon (some-> status (str) (s/lower-case)))]
    [:div {:style {:display :flex
                   :align-items :center
                   :gap 6}}
     (when icon-type
       [alc/material-icon {:icon-type icon-type
                           :style {:color (style/alc-color color)
                                   :font-size 16}}])
     [:span (if (not-empty (str status))
              (util/->capitalize-words status)
              "-")]]))

(defn- cell-label [{:keys [numeric? money?]} v]
  (cond
    money? (money-label v)
    (nil? v) "-"
    numeric? (util/readable-number v)
    :else (str v)))

(defn orders-header
  "The sticky page header. Reports its height so the table's sticky header can start
   below it instead of scrolling underneath."
  []
  (let [container-id "orders-header-container"
        orders-count- (re/subscribe [:voice-commerce/orders-count-])
        vs- (re/subscribe [:core/viewport-size])
        set-header-height #(when-let [h (some-> (js/document.getElementById container-id)
                                                (.getBoundingClientRect)
                                                (.-height))]
                             (re/dispatch [:voice-commerce/orders-set-header-height h]))]
    (re/create-class
      {:component-did-mount set-header-height
       :component-did-update set-header-height
       :reagent-render
       (fn []
         @vs-
         [:div {:id container-id
                :style {:background-color (style/alc-color "Background/card_hex")
                        :display :flex
                        :flex-direction :column
                        :gap 16
                        :padding "12px 16px 16px"
                        :position :sticky
                        :top 0
                        :left 0
                        :z-index (inc (style/z :medium))}}
          [:div {:style {:display :flex
                         :align-items :center
                         :justify-content :space-between}}
           [:span {:style {:color (style/alc-color "Text/primary_header")
                           :font-size 24
                           :font-weight 400
                           :line-height 1.33}}
            "Orders"
            (when-not (util/loading? @orders-count-)
              [:span {:style {:color (style/alc-color "Text/primary")
                              :margin-left 8}}
               (str "(" (some-> @orders-count- (util/readable-number)) ")")])]
           ;; Searching is just deep-linking: put the id in the URL and the panel the
           ;; route opens fetches it (the sessions convo-id search works the same way).
           (let [search-order-id (fn [e]
                                   (when-let [el (js/document.getElementById "order-id-search-input")]
                                     (let [v (or (some-> e
                                                         (.-clipboardData)
                                                         (.getData "text/plain"))
                                                 (.-value el))]
                                       (re/dispatch [:router/go-with-merged-query-params
                                                     {:order (orders.d/order->order-qp {:order-id v})}])
                                       (set! (.-value el) "")
                                       (.blur el))))]
             [alc/up-input {:id :order-id-search-input
                            :class-name "w-60"
                            :placeholder "Search By Order ID"
                            :on-key-down #(when (util/enter-key-event? %)
                                            (search-order-id %))
                            :on-paste search-order-id}])]])})))

(defn orders-list []
  (let [orders- (re/subscribe [:voice-commerce/orders-list-flattened-])
        loading?- (re/subscribe [:voice-commerce/orders-list-loading?-])
        has-more?- (re/subscribe [:voice-commerce/orders-list-data-has-more?-])
        next-page-loading?- (re/subscribe [:voice-commerce/orders-list-next-page-loading?-])
        next-page- (re/subscribe [:voice-commerce/orders-list-next-page-])
        header-height- (re/subscribe [:voice-commerce/orders-header-height-])
        selected-order- (re/subscribe [:voice-commerce/selected-order-])
        expanded* (re/ratom #{})]
    (fn []
      [alc/table {:no-data-text "No orders to display"
                  :header-top @header-height-
                  :loading? @loading?-
                  :loading-more? @next-page-loading?-
                  :scroll-container-id l/core-scroll-content-id
                  :bottom-px-buffer 200
                  :on-bottom-buffer (when @has-more?-
                                      #(re/dispatch [:voice-commerce/orders-load-list-data
                                                     [nil {:page @next-page-}]]))
                  :has-more-rows? true
                  :sticky-headers? true
                  :no-data-material-icon :inventory_2
                  :row-style {:height :auto
                              :align-items :center}
                  :flex-vals (conj (mapv :flex (visible-columns @selected-order-))
                                   (str "0 0 " expand-column-width "px"))
                  :headers (conj (mapv :label (visible-columns @selected-order-)) "")
                  ;; An expanded order adds a row beneath its own: a single cell, which
                  ;; the table lays out full width since its count can't match flex-vals.
                  :rows (into []
                              (comp (map-indexed vector)
                                    (mapcat (fn [[idx order]]
                                              (let [row-key (or (:order-id order) idx)
                                                    expanded? (contains? @expanded* row-key)
                                                    selected? (= (:order-id @selected-order-) (:order-id order))
                                                    ;; Selected (panel open) outranks expanded; the panel row
                                                    ;; below keeps the plain background either way.
                                                    row-background (cond
                                                                     selected? (style/alc-color "Background/active_secondary")
                                                                     expanded? (style/alc-color "Background/hover")
                                                                     :else nil)
                                                    order-row {:key (str row-key)
                                                               :style (when row-background
                                                                        {:background-color row-background})
                                                               :hover-style (when row-background
                                                                              {:background-color row-background})
                                                               ;; No `:open?` — the event toggles, as the
                                                               ;; sessions list row does.
                                                               :on-click #(re/dispatch
                                                                            [:voice-commerce/orders-toggle-drawer-open
                                                                             {:order-id (:order-id order)
                                                                              :order-idx idx}])
                                                               :cells (conj (mapv (fn [{:keys [key] :as column}]
                                                                               (let [v (get order key)
                                                                                     label (cell-label column v)]
                                                                                 (case key
                                                                                   :order-id {:label label
                                                                                              :copyable? true
                                                                                              :style {:color (style/alc-color "Text/secondary")
                                                                                                      :white-space :nowrap}}
                                                                                   :occurred-at {:label [:span {:style {:color (style/alc-color "Text/secondary")}}
                                                                                                         label]}
                                                                                   :order-status {:label [status-cell v]}
                                                                                   :total {:label label
                                                                                           :style {:color (style/alc-color "Common/White")}}
                                                                                   {:label label})))
                                                                             (visible-columns @selected-order-))
                                                                       {:label [:div {:style {:display :flex
                                                                                              :align-items :center
                                                                                              :width "100%"}}
                                                                                [expand-toggle
                                                                                 {:expanded? expanded?
                                                                                  :on-toggle #(swap! expanded*
                                                                                                     (fn [ks]
                                                                                                       (if (contains? ks row-key)
                                                                                                         (disj ks row-key)
                                                                                                         (conj ks row-key))))}]]
                                                                        :style {:display :flex
                                                                                :justify-content :flex-end
                                                                                :padding 0}})}]
                                                (cond-> [order-row]
                                                        expanded?
                                                        (conj {:key (str row-key "-details")
                                                               :style {:padding 0}
                                                               :cells [{:label [details-panel order]
                                                                        :style {:padding 0}}]}))))))
                              @orders-)}])))

(defn root [_]
  (let [orders- (re/subscribe [:voice-commerce/orders-list-flattened-])
        loading?- (re/subscribe [:voice-commerce/orders-list-loading?-])
        ;; Reopen the panel the URL names, so a refresh or a shared link lands on
        ;; the same order (the sessions root does this with its `conv` param).
        open-order-from-qp (fn [this]
                             (when-let [order (some-> (:order (re/props this))
                                                      (orders.d/order-qp->order))]
                               (re/dispatch [:voice-commerce/orders-toggle-drawer-open
                                             (assoc order :open? true)])))]
    (re/create-class
      {:component-did-mount (fn [this]
                              (open-order-from-qp this)
                              (re/dispatch [:voice-commerce/orders-initialize]))
       :component-did-update open-order-from-qp
       :reagent-render
       (fn [args]
         [l/logged-in-with-header-nav-voice-commerce
          (merge args {:concept-dropdown? true
                       :location-dropdown? true})
          [:div {:style {:display :flex
                         :flex-direction :column
                         :width "100%"
                         :min-height "100%"}}
           [orders-header]
           [:div {:style {:flex 1
                          :display :flex
                          :flex-direction :column
                          :padding "0 16px 12px"}}
            (cond
              @loading?- [empty-state/loading-orders-card]
              (empty? @orders-) [empty-state/no-orders-card]
              :else [orders-list])]]])})))
